import { cloneElement, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useUserQuery } from "../../services/queryHooks";
import { Loading } from "../../components/common/Loading";
import { Edit, Email, Password, Delete } from "@mui/icons-material";
import UserDataSettingsModal from "./UserDataSettingsModal";
import ChangeEmailModal from "./ChangeEmailModal";
import ChangePasswordModal from "./ChangePasswordModal";
import DeleteAccountModal from "./DeleteAccountModal";

export default function Settings() {
	const { t, i18n } = useTranslation();
	const theme = useTheme();

	const { data: userData, isPending: userDataLoading } = useUserQuery();
	const userCurrency = localStorage.getItem("userCurrency");

	const [isUserDataSettingsModalOpen, setIsUserDataSettingModalOpen] =
		useState(false);
	const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
	const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
		useState(false);
	const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
		useState(false);

	const accountButtons = [
		{
			label: "changeEmail",
			icon: <Email />,
			onClick: () => setIsChangeEmailModalOpen(true),
		},
		{
			label: "changePassword",
			icon: <Password />,
			onClick: () => setIsChangePasswordModalOpen(true),
		},
		{
			label: "deleteAccount",
			icon: <Delete color="error" />,
			color: "error",
			onClick: () => setIsDeleteAccountModalOpen(true),
		},
	];

	const tileStyles = {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 1,
		backgroundColor: theme.palette.background.paper,
		borderRadius: "10px",
		p: 2,
		flexGrow: 1,
	};

	function ButtonTile({ icon, label, color, onClick }) {
		return (
			<Button sx={tileStyles} onClick={onClick}>
				{icon &&
					cloneElement(icon, {
						sx: {
							fontSize: "2.5rem",
							color,
						},
					})}

				<Typography variant="body1">
					<span
						style={{
							fontWeight: 600,
							color:
								color === "error" && theme.palette.error.main,
						}}
					>
						{t(label)}
					</span>
				</Typography>
			</Button>
		);
	}

	const boxStyles = {
		display: "flex",
		flexDirection: "column",
		alignSelf: "start",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "10px",
		m: 2,
		p: 2,
	};

	const oneRowStyles = {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
	};
	return (
		<>
			{userDataLoading && <Loading />}
			{isUserDataSettingsModalOpen && (
				<UserDataSettingsModal
					isOpen={isUserDataSettingsModalOpen}
					onClose={() => setIsUserDataSettingModalOpen(false)}
					initialData={{
						name: userData?.name ?? "",
						surname: userData?.surname ?? "",
						currency: userCurrency ?? "",
						language: i18n.language ?? "",
					}}
				/>
			)}
			{isChangeEmailModalOpen && (
				<ChangeEmailModal
					isOpen={isChangeEmailModalOpen}
					onClose={() => setIsChangeEmailModalOpen(false)}
				/>
			)}
			{isChangePasswordModalOpen && (
				<ChangePasswordModal
					isOpen={isChangePasswordModalOpen}
					onClose={() => setIsChangePasswordModalOpen(false)}
				/>
			)}
			{isDeleteAccountModalOpen && (
				<DeleteAccountModal
					isOpen={isDeleteAccountModalOpen}
					onClose={() => setIsDeleteAccountModalOpen(false)}
				/>
			)}
			<Box sx={boxStyles}>
				<Typography variant="h6">{t("accountSettings")}</Typography>
			</Box>
			<Box sx={boxStyles}>
				<Box sx={oneRowStyles}>
					<Box>
						<Typography variant="body1">
							{t("nameOfUser")}
							{": "}
							<span style={{ fontWeight: 600 }}>
								{userData?.name}
							</span>
						</Typography>

						<Typography variant="body1">
							{t("surname")}
							{": "}
							<span style={{ fontWeight: 600 }}>
								{userData?.surname}
							</span>
						</Typography>

						<Typography variant="body1">
							{t("currency")}
							{": "}
							<span style={{ fontWeight: 600 }}>
								{userCurrency}
							</span>
						</Typography>

						<Typography variant="body1">
							{t("language")}
							{": "}
							<span style={{ fontWeight: 600 }}>
								{i18n.language}
							</span>
						</Typography>

						<Typography variant="body1">
							{t("email")}
							{": "}
							<span style={{ fontWeight: 600 }}>
								{userData?.email}
							</span>
						</Typography>
					</Box>
					<Button
						variant="contained"
						startIcon={<Edit />}
						onClick={() => setIsUserDataSettingModalOpen(true)}
					>
						Edit Info
					</Button>
				</Box>
			</Box>

			<Box
				sx={{
					display: "flex",
					gap: 2,
					justifyContent: "space-between",
					m: 2,
				}}
			>
				{accountButtons.map((acc) => (
					<ButtonTile
						key={acc.label}
						icon={acc.icon}
						label={acc.label}
						color={acc.color}
						onClick={acc.onClick}
					/>
				))}
			</Box>
		</>
	);
}
